import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-xl text-sm font-medium ring-offset-background transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 active:scale-[0.98]",
  {
    variants: {
      variant: {
        default:
          "bg-gradient-to-br from-primary to-primary/80 text-primary-foreground shadow-[0_4px_20px_-4px_hsl(var(--primary)/0.5)] hover:shadow-[0_8px_30px_-4px_hsl(var(--primary)/0.6)] hover:from-primary hover:to-primary",
        destructive:
          "bg-gradient-to-br from-destructive to-destructive/80 text-destructive-foreground shadow-[0_4px_20px_-4px_hsl(var(--destructive)/0.5)] hover:shadow-[0_8px_30px_-4px_hsl(var(--destructive)/0.6)]",
        outline:
          "border border-border bg-card text-foreground shadow-[var(--shadow-input)] hover:bg-muted hover:border-primary/40",
        secondary:
          "bg-muted border border-border text-secondary-foreground hover:bg-secondary/80",
        ghost: "hover:bg-muted hover:text-foreground",
        link: "text-primary underline-offset-4 hover:underline",
        premium:
          "bg-gradient-to-br from-primary via-primary to-accent text-primary-foreground shadow-[0_8px_30px_-4px_hsl(var(--primary)/0.55)] hover:shadow-[0_12px_40px_-4px_hsl(var(--primary)/0.7)] font-semibold",
        gold:
          "bg-gradient-to-br from-gold to-[hsl(36_78%_48%)] text-gold-foreground shadow-[0_8px_30px_-4px_hsl(var(--gold)/0.45)] hover:shadow-[0_12px_40px_-4px_hsl(var(--gold)/0.6)] font-semibold",
        success:
          "bg-gradient-to-br from-success to-[hsl(160_76%_36%)] text-success-foreground shadow-[0_4px_20px_-4px_hsl(var(--success)/0.5)] hover:shadow-[0_8px_30px_-4px_hsl(var(--success)/0.6)]",
      },
      size: {
        default: "h-10 px-5 py-2",
        sm: "h-9 px-3.5 text-xs",
        lg: "h-12 px-8 text-base",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />;
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
